import React, { useCallback } from 'react';
import { Dialog } from '@mui/material';
import { IconButton } from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";

import Timeline from '@material-ui/lab/Timeline';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineDot from '@material-ui/lab/TimelineDot';
import TimelineOppositeContent from '@material-ui/lab/TimelineOppositeContent';
import Typography from '@material-ui/core/Typography';
import moment from 'moment';

export const stepLabels = {
    "additional_document": "Documento adicional",
    "authentication_code": "Código de autenticação",
    "credit_analysis_reproved": "Análise de crédito reprovada",
    "device_scan": "Escaneamento de dispositivo",
    "document_display": "Exibição dos documentos",
    "enrich_personal_data": "Dados pessoais",
    "face_recognition": "Reconhecimento de face",
    "face_validation_document_fallback": "Documento pessoal (fallback de validação facial)",
    "folder_authentication_code": "Código de autenticação (pasta)",
    "folder_face_recognition": "Reconhecimento de face (pasta)",
    "folder_personal_document": "Documento pessoal (pasta)",
    "fraud_warning": "Aviso de fraude",
    "generic_term": "Termo genérico",
    "lending_analysis": "Análise de crédito",
    "login": "Login",
    "personal_document": "Documento pessoal",
    "portability_and_refinancing_summary": "Resumo de portabilidade e refinanciamento",
    "refinancing_summary": "Resumo do refinanciamento",
    "resume_insurance": "Resumo do seguro da operação",
    "resume_operation": "Resumo da operação",
    "signed": "Assinatura realizada",
    "signed_from_api": "Assinatura realizada via API",
    "social_security_cards": "Cartão previdenciário",
    "term_of_authorization": "Termo de autorização",
    "terms_of_use": "Termos de uso",
}

export const statusLabels = {
    "created": "Criado",
    "submitted": "Pronto para envio",
    "on_signature": "Em assinatura",
    "signed": "Assinado",
    "sending_sign_receipt": "Enviando recibo de assinatura",
    "receipt_sent": "Recibo de assinatura enviado",
    "analyzing": "Em análise",
    "analyzed": "Analisado",
    "completed": "Concluído",
    "canceled": "Cancelado",
    "expired": "Expirado",
    "removed": "Removido",
    "failed_waiting_for_manual_fix": "Falha aguardando correção manual",
    "waiting_folder_conclusion": "Aguardando conclusão da pasta",
}

export const eventTypeLabels = {
    "step": "Etapa",
    "status": "Status",
    "link": "Link",
}

export const buildSignerEvents = (signer) => {
    const events = []
    if (signer.link_first_clicked_at) {
        events.push({
            type: 'link',
            label: 'Primeiro clique no link de assinatura',
            event_date: signer.link_first_clicked_at,
        })
    }
    for (const stepEvent of (signer.signer_signature_step_events || [])) {
        events.push({
            type: 'step',
            enum: stepEvent.step,
            label: stepLabels[stepEvent.step] || stepEvent.step,
            event_date: stepEvent.event_date,
            event_data: stepEvent.event_data,
        })
    }
    for (const statusEvent of (signer.signer_signature_status_events || [])) {
        events.push({
            type: 'status',
            enum: statusEvent.status,
            label: statusLabels[statusEvent.status] || statusEvent.status,
            event_date: statusEvent.event_date,
            event_data: statusEvent.event_data,
        })
    }
    events.sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
    return events
}

const eventDotColors = {
    "status": "#525252",
}

export const EventsTimeline = ({ events }) => (
    <Timeline align="left" style={{ padding: '0' }}>
        {events.map((event, index) => (
            <TimelineItem key={`${event.type}-${event.event_date}-${index}`} style={{ minHeight: '30px' }}>
                <TimelineOppositeContent style={{ flex: '0 0 90px', padding: '0', paddingTop: '5px', paddingRight: '10px' }} >
                    {event.event_date && <>
                        <Typography color="textSecondary" ><strong>{moment(event.event_date).format('DD/MM/YYYY')}</strong></Typography>
                        <Typography color="textSecondary">{moment(event.event_date).format('HH:mm:ss')}</Typography>
                    </>}
                </TimelineOppositeContent>
                <TimelineSeparator>
                    <TimelineDot style={{ padding: 2, backgroundColor: event.dotColor || eventDotColors[event.type] || "#0b1f82" }} />
                    {index <= events.length - 2 && !event.hideConnector && <TimelineConnector style={{ backgroundColor: "#0b1f82" }} />}
                </TimelineSeparator>
                <TimelineContent style={{ flex: '1', minWidth: 0, padding: '0', paddingTop: '5px', paddingLeft: '10px' }}>
                    <Typography style={{ marginBottom: '5px', color: '#888' }}>{event.typeLabel || eventTypeLabels[event.type]}</Typography>
                    <Typography style={{ marginBottom: '5px' }}><strong>{event.label}</strong></Typography>
                    {event.content}
                    <hr style={{ color: '#0b1f82' }} />
                </TimelineContent>
            </TimelineItem>
        ))}
    </Timeline>
)

function SignerSignatureEventsDialog(props) {

    const {
        open,
        setOpen,
        signerEventsSelected,
        setSignerEventsSelected,
    } = props

    const onCloseDialog = useCallback(() => {
        setSignerEventsSelected(undefined)
        setOpen(false)
    }, [setSignerEventsSelected, setOpen])

    const events = buildSignerEvents(signerEventsSelected)

    return (
        <Dialog
            open={open}
            onClose={() => onCloseDialog()}
            fullWidth
            maxWidth={'sm'}
        >
            <div className="dialog__column-container">
                <header style={{ marginBottom: '10px' }}>
                    <h1 style={{ fontSize: '22px', color: "#0b1f82" }}>Eventos de assinatura</h1>
                    <IconButton
                        onClick={() => onCloseDialog()}
                    >
                        <CloseIcon />
                    </IconButton>
                </header>
                <div style={{ marginBottom: '20px' }}>
                    <p>
                        <span style={{ fontSize: '16px' }}><strong>Nome:</strong></span>
                        <span style={{ fontSize: '16px', marginLeft: '10px' }}>{signerEventsSelected.name}</span>
                    </p>
                </div>
                {
                    events.length > 0 ?
                        <div style={{ padding: '10px 30px 0px 30px' }}>
                            <EventsTimeline events={events} />
                        </div>
                        : <div>Nenhum evento de assinatura disponível para este assinante</div>
                }
            </div>
        </Dialog>
    )
}

export default SignerSignatureEventsDialog
